<template>
  <v-container fluid>
    <v-row ref="filtersContainer" class="mb-1">
      <v-col cols="6" class="d-flex align-center">
        <span class="text-h5">
          <v-icon color="black" class="pb-1">mdi-cart-remove</v-icon> Возвраты
        </span>
        <template v-if="userService.can('return_add_button')">
          <v-btn class="ml-3" style="vertical-align: middle;" rounded color="primary" small
                 @click="showForm()"
          >
            <v-icon left>mdi-plus</v-icon> Добавить
          </v-btn>
          <v-dialog v-model="formDialog" width="60vw" persistent>
            <consignment-return-form v-if="formDialog"
                                     @consignment-return:form:saved="closeForm"
                                     @consignment-return:form:cancel="closeForm"
            ></consignment-return-form>
          </v-dialog>
        </template>
      </v-col>
      <v-col cols="2">
        <v-autocomplete v-model="filter.counteragent" :items="counteragents"
                        item-value="id" item-text="full_name"
                        prepend-inner-icon="mdi-account-group" label="Контрагент"
                        :loading="!!counteragentsLoading" :disabled="!!counteragentsLoading"
                        clearable hide-details
        ></v-autocomplete>
      </v-col>
      <v-col cols="2">
        <v-menu v-model="fromDateMenu" transition="scale-transition" offset-y max-width="290px" min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field :value="fromDateFormatted"
                          label="с" prepend-inner-icon="mdi-calendar"
                          readonly v-bind="attrs" v-on="on" hide-details
            ></v-text-field>
          </template>
          <v-date-picker v-model="filter.reg_date_after" :disabled="!!fetching"></v-date-picker>
        </v-menu>
      </v-col>
      <v-col cols="2">
        <v-menu v-model="toDateMenu" transition="scale-transition" bottom left offset-y max-width="290px" min-width="auto">
          <template v-slot:activator="{ on, attrs }">
            <v-text-field :value="toDateFormatted"
                          label="по" prepend-inner-icon="mdi-calendar"
                          readonly v-bind="attrs" v-on="on" hide-details
            ></v-text-field>
          </template>
          <v-date-picker v-model="filter.reg_date_before" :disabled="!!fetching"></v-date-picker>
        </v-menu>
      </v-col>
    </v-row>

    <v-data-table
      :headers="nonSortableHeaders"
      :items="consignmentReturns"
      :loading="!!fetching" :items-per-page="filter.limit"
      :height="dataTableHeight" disable-items-per-page hide-default-footer fixed-header
      no-data-text="Нет данных" loading-text="Данные загружаются"
    >
      <template #item="{item, index}">
        <tr>
          <td class="text-center">{{filter.limit * (filter.page - 1) + index + 1}}</td>
          <td>{{item.reg_number}}</td>
          <td>{{item.reg_date|date}}</td>
          <td>{{item.counteragent_name}}</td>
          <td>Фактура #{{item.doc}}</td>
          <td class="text-right">
            <v-btn class="ml-2" text small depressed color="success" @click="showDetails(item)">
              <v-icon left small>mdi-eye</v-icon> Показать
            </v-btn>

            <v-btn class="ml-2" icon small depressed color="error" @click="askRemove(item)" :loading="deleting[item.id]">
              <v-icon small>mdi-delete</v-icon>
            </v-btn>
          </td>
        </tr>
      </template>
    </v-data-table>

    <pagination ref="paginationContainer" :limit.sync="limit" :page.sync="page" :total-pages="totalPages"></pagination>

    <v-dialog v-model="detailDialog" width="70vw">
      <consignment-return-details v-if="detailDialog"
                                  @dialog:close="closeDetails"
      ></consignment-return-details>
    </v-dialog>
  </v-container>
</template>

<script>
  import {mapState as defaultMapState, createNamespacedHelpers} from 'vuex'
  const {mapState, mapActions, mapMutations} = createNamespacedHelpers('Consignment')

  import {hasFilter, hasDialog} from '@/mixins'

  import ConsignmentReturnForm from '@/modules/consignment/return/form'
  import ConsignmentReturnDetails from '@/modules/consignment/return/details'

  import moment from 'moment'
  import {SERVER_DATE_FORMAT, USER_DATE_FORMAT} from '@/filters'

  export default {
    name: 'consignment-return-list',

    mixins: [hasFilter, hasDialog],

    customFilters: {
      counteragent: null,
      reg_date_before: moment().format(SERVER_DATE_FORMAT),
      reg_date_after: moment().subtract(1, 'month').format(SERVER_DATE_FORMAT),
    },

    created() {
      this.fetch(this.filter)
    },

    computed: {
      ...mapState(['deleting', 'consignmentReturn']),
      ...mapState({
        fetching: 'fetchingConsignmentReturns',
        data: 'consignmentReturns',
      }),

      ...defaultMapState('Counteragent', {
        counteragents: state => state.filterData?.results || [],
        counteragentsLoading: state => !!state.fetching,
      }),

      headers() {
        return [
          {text: '#', align: 'center', width: '100'},
          {text: 'Рег номер'},
          {text: 'Дата'},
          {text: 'Контрагент'},
          {text: 'Фактура'},
          {text: 'Действия', align: 'right', sortable: false},
        ]
      },

      consignmentReturns() {
        return this.data?.results || []
      },

      fromDateFormatted() {
        return moment(this.filter.reg_date_after).format(USER_DATE_FORMAT)
      },

      toDateFormatted() {
        return moment(this.filter.reg_date_before).format(USER_DATE_FORMAT)
      },
    },

    methods: {
      ...mapActions({
        fetch: 'fetchReturns',
        showReturn: 'showReturn',
        showConsignment: 'show',
        remove: 'removeReturn',
      }),
      ...mapMutations({
        setModel: 'setReturn',
      }),

      closeDetails() {
        this.setModel({})
        this.detailDialog = false
      },

      show(id) {
        this.showReturn(id)
            .then(() => {
              this.showConsignment(this.consignmentReturn.doc)
            })
      },

      showForm(item) {
        this.setModel({})
        this.formDialog = true
      },

      closeForm() {
        this.formDialog = false
        this.setModel({})
        this.fetch(this.filter)
      },
    },

    mounted() {
      this.$eventBus.$emit('setTitle', 'Возвраты')
      this.$store.dispatch('Counteragent/fetchFilterList')
    },

    components: {
      ConsignmentReturnForm,
      ConsignmentReturnDetails,
    }
  }
</script>
